import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, Button, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Switch, FormControlLabel, Chip, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SyncIcon from '@mui/icons-material/Sync';
import { adminApi } from '../../api';

const EMPTY = {
  ae_title: '',
  description: '',
  facility_id: '',
  storage_backend_id: '',
  calling_ae_titles_text: '',
  host: 'arc',
  port: 11112,
  enabled: true,
  dcm_storage_id: '',
  dcm_storage_uri: '',
};

const fieldSx = {
  mb: 2,
  '& .MuiOutlinedInput-root': { borderRadius: 0 },
  '& .MuiInputLabel-root': { color: '#666' },
  '& input': { color: '#e0e0e0' },
  '& textarea': { color: '#e0e0e0' },
};
const selectSx = {
  mb: 2,
  '& .MuiOutlinedInput-root': { borderRadius: 0 },
  '& .MuiInputLabel-root': { color: '#666' },
  '& .MuiSelect-select': { color: '#e0e0e0' },
};
const pill = (label, color, bg) => <Chip label={label} size="small" sx={{ height: 18, fontSize: 10, borderRadius: 0, color, bgcolor: bg }} />;

const normalizeForEdit = (row) => ({
  ...row,
  storage_backend_id: row.storage_backend_id || '',
  calling_ae_titles_text: (row.calling_ae_titles || []).join(', '),
  dcm_storage_id: row.dcm_storage_id || '',
  dcm_storage_uri: row.dcm_storage_uri || '',
});

const payloadFromDialog = (data) => ({
  ae_title: String(data.ae_title || '').trim().toUpperCase(),
  description: data.description || null,
  facility_id: data.facility_id,
  storage_backend_id: data.storage_backend_id || null,
  calling_ae_titles: String(data.calling_ae_titles_text || '')
    .split(',')
    .map(v => v.trim().toUpperCase())
    .filter(Boolean),
  host: data.host || 'arc',
  port: Number(data.port || 11112),
  enabled: data.enabled ?? true,
  dcm_storage_id: data.dcm_storage_id || null,
  dcm_storage_uri: data.dcm_storage_uri || null,
});

const DicomAeTitles = () => {
  const [aeTitles, setAeTitles] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [storageBackends, setStorageBackends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [error, setError] = useState('');

  const load = () => Promise.all([
    adminApi.getDicomAeTitles(),
    adminApi.getFacilities(),
    adminApi.getStorageBackends(),
  ]).then(([a, f, b]) => {
    setAeTitles(a);
    setFacilities(f.filter(x => x.name !== '__DEFAULT__' && x.enabled !== false));
    setStorageBackends(b.filter(x => x.enabled !== false));
  }).catch(e => setError(e.response?.data?.detail || e.message)).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);
  const close = () => { setDialog(null); setError(''); };
  const upd = (key, value) => setDialog(d => ({ ...d, data: { ...d.data, [key]: value } }));

  const selectedFacility = facilities.find(f => f.id === dialog?.data?.facility_id);
  const inheritedStorageId = selectedFacility?.storage_backend_id || '';
  const selectedStorageId = dialog?.data?.storage_backend_id || inheritedStorageId;
  const selectedStorage = storageBackends.find(b => b.id === selectedStorageId);

  const save = async () => {
    setSaving(true); setError('');
    try {
      const payload = payloadFromDialog(dialog.data);
      if (dialog.mode === 'create') await adminApi.createDicomAeTitle(payload);
      else {
        const { ae_title, ...updatePayload } = payload;
        await adminApi.updateDicomAeTitle(dialog.data.id, updatePayload);
      }
      close(); await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
    setSaving(false);
  };


  const syncToDcm4chee = async (id) => {
    setSyncingId(id); setError('');
    try {
      await adminApi.syncDicomAeTitle(id);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
    setSyncingId(null);
  };

  const del = async (id) => {
    if (!window.confirm('Delete this DICOM AE title mapping?')) return;
    await adminApi.deleteDicomAeTitle(id);
    load();
  };

  const field = (key, label, opts = {}) => (
    <TextField key={key} label={label} size="small" fullWidth value={dialog?.data?.[key] ?? ''}
      onChange={e => upd(key, e.target.value)} sx={fieldSx} {...opts} />
  );

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography sx={{ color: 'primary.main', fontSize: 20, fontWeight: 700, letterSpacing: 1 }}>DICOM AE Titles</Typography>
          <Typography sx={{ color: '#555', fontSize: 12 }}>Map inbound AE titles to facilities and archive storage policies.</Typography>
        </Box>
        <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={() => setDialog({ mode: 'create', data: { ...EMPTY } })}
          sx={{ borderRadius: 0, borderColor: '#333', color: '#aaa', '&:hover': { borderColor: 'primary.main', color: 'primary.main' } }}>
          Add AE Title
        </Button>
      </Box>
      {error && <Typography sx={{ color: '#ff8a80', fontSize: 12, mb: 1 }}>{String(error)}</Typography>}

      <TableContainer component={Paper} sx={{ bgcolor: '#111', border: '1px solid #1e1e1e', borderRadius: 1 }}>
        <Table size="small">
          <TableHead><TableRow>{['AE Title','Facility','Storage','Calling AEs','Endpoint','Status',''].map(h => (
            <TableCell key={h} sx={{ bgcolor: '#0d0d0d', color: 'primary.main', fontSize: 11, fontWeight: 700, letterSpacing: 0.8, borderBottom: '1px solid #222' }}>{h.toUpperCase()}</TableCell>
          ))}</TableRow></TableHead>
          <TableBody>{aeTitles.map(a => (
            <TableRow key={a.id} sx={{ '&:hover': { bgcolor: '#141414' } }}>
              <TableCell sx={{ color: '#e0e0e0', fontSize: 13, fontFamily: 'monospace', borderBottom: '1px solid #1a1a1a' }}>{a.ae_title}<Typography sx={{ color: '#555', fontSize: 11 }}>{a.description || '—'}</Typography></TableCell>
              <TableCell sx={{ color: '#aaa', fontSize: 12, borderBottom: '1px solid #1a1a1a' }}>{a.facility_display_name || a.facility_name}</TableCell>
              <TableCell sx={{ color: '#aaa', fontSize: 12, borderBottom: '1px solid #1a1a1a' }}>
                {a.storage_backend_name || 'Facility/default'}<Typography sx={{ color: '#555', fontSize: 10, fontFamily: 'monospace' }}>{a.dcm_storage_id || a.storage_backend_uri || '—'}</Typography>
              </TableCell>
              <TableCell sx={{ color: '#777', fontSize: 11, borderBottom: '1px solid #1a1a1a', fontFamily: 'monospace' }}>{(a.calling_ae_titles || []).join(', ') || 'Any'}</TableCell>
              <TableCell sx={{ color: '#777', fontSize: 11, borderBottom: '1px solid #1a1a1a', fontFamily: 'monospace' }}>{a.host}:{a.port}</TableCell>
              <TableCell sx={{ borderBottom: '1px solid #1a1a1a' }}>{a.enabled ? pill(a.dcm4chee_synced_at && !a.dcm4chee_sync_error ? 'SYNCED' : 'ACTIVE', a.dcm4chee_synced_at && !a.dcm4chee_sync_error ? '#66bb6a' : '#00bcd4', a.dcm4chee_synced_at && !a.dcm4chee_sync_error ? '#003322' : '#002233') : pill('DISABLED', '#555', '#1a1a1a')}
                {a.dcm4chee_sync_error && <Typography sx={{ color: '#ff8a80', fontSize: 9, maxWidth: 180 }} noWrap>{a.dcm4chee_sync_error}</Typography>}
              </TableCell>
              <TableCell sx={{ borderBottom: '1px solid #1a1a1a' }}>
                <Tooltip title="Sync to dcm4chee LDAP"><span><IconButton size="small" disabled={syncingId === a.id} onClick={() => syncToDcm4chee(a.id)} sx={{ color: '#555', '&:hover': { color: '#66bb6a' } }}>{syncingId === a.id ? <CircularProgress size={14} /> : <SyncIcon fontSize="small" />}</IconButton></span></Tooltip>
                <Tooltip title="Edit"><IconButton size="small" onClick={() => setDialog({ mode: 'edit', data: normalizeForEdit(a) })} sx={{ color: '#555', '&:hover': { color: 'primary.main' } }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Delete"><IconButton size="small" onClick={() => del(a.id)} sx={{ color: '#555', '&:hover': { color: '#ef9a9a' } }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
              </TableCell>
            </TableRow>
          ))}</TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!dialog} onClose={close} maxWidth="md" fullWidth PaperProps={{ sx: { bgcolor: '#111', border: '1px solid #222', borderRadius: 1 } }}>
        <DialogTitle sx={{ color: 'primary.main', fontSize: 14, letterSpacing: 1 }}>{dialog?.mode === 'create' ? 'ADD DICOM AE TITLE' : 'EDIT DICOM AE TITLE'}</DialogTitle>
        <DialogContent><Box sx={{ pt: 1 }}>
          {field('ae_title', 'AE Title', { disabled: dialog?.mode === 'edit', helperText: '1–16 chars: A-Z, 0-9, _, ., $, -' })}
          {field('description', 'Description')}
          <FormControl fullWidth size="small" sx={selectSx}>
            <InputLabel>Facility</InputLabel>
            <Select value={dialog?.data?.facility_id || ''} label="Facility" onChange={e => upd('facility_id', e.target.value)}>
              {facilities.map(f => <MenuItem key={f.id} value={f.id}>{f.display_name || f.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={selectSx}>
            <InputLabel>Storage Backend</InputLabel>
            <Select value={dialog?.data?.storage_backend_id || ''} label="Storage Backend" onChange={e => upd('storage_backend_id', e.target.value)}>
              <MenuItem value="">Inherit from selected facility{selectedFacility ? ` (${selectedFacility.storage_backend_name || 'default'})` : ''}</MenuItem>
              {storageBackends.map(b => <MenuItem key={b.id} value={b.id}>{b.display_name || b.name} — {b.storage_tier} — {b.base_uri}</MenuItem>)}
            </Select>
          </FormControl>
          {selectedStorage && <Typography sx={{ color: '#607d8b', fontSize: 11, mb: 2 }}>Effective storage: {selectedStorage.name} → {selectedStorage.base_uri}</Typography>}
          {field('calling_ae_titles_text', 'Allowed Calling AE Titles (comma separated, optional)')}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flex: 1 }}>{field('host', 'Service Host / Load Balancer')}</Box>
            <Box sx={{ width: 140 }}>{field('port', 'DICOM Port', { type: 'number' })}</Box>
          </Box>
          {field('dcm_storage_id', 'dcm4chee Storage ID Override (optional)')}
          {field('dcm_storage_uri', 'dcm4chee Storage URI Override (optional)')}
          <FormControlLabel control={<Switch checked={dialog?.data?.enabled ?? true} onChange={e => upd('enabled', e.target.checked)} size="small" />} label={<Typography sx={{ color: '#aaa', fontSize: 13 }}>Enabled</Typography>} />
        </Box></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}><Button onClick={close} sx={{ color: '#555' }}>Cancel</Button><Button onClick={save} disabled={saving || !dialog?.data?.facility_id || !dialog?.data?.ae_title} variant="outlined" sx={{ borderRadius: 0, borderColor: 'primary.main', color: 'primary.main' }}>{saving ? 'Saving…' : 'Save'}</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default DicomAeTitles;
